import React from 'react';
import _, {isEmpty} from 'lodash';
import {MenuItem} from '@mui/material';
import Grid from '@mui/material/Grid';
import AgyleSelectField from '../../ui/selector/agyleSelectField';
import EditorMarkdownComponent from '../../editor/editorMarkdown';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import useDict from '../../../hooks/useDict';
import {addNoteForPatient} from '../../../store/reducers/notes';
import {saveAgylePreference} from '../../../store/reducers/preferences';
import {hasOneOfPermission, Permissions, user} from '../../../store/reducers/user';
import Formatter, {useWindowSize} from '../../../util/formatter';
import AgyleDialog from '../../modal/agyleDialog';

const NewNoteModal = ({
                          open,
                          onClose,
                          patient,
                          disableSave,
                          isEDAU,
                          fromForm,
                          forceCurrentAttendance = false,
                      }) => {
        const {t} = useTranslation();
        const dispatch = useDispatch();

        // User
        const userObject = useSelector(user);
        const tester = Formatter.formatUserFullName(userObject);

        // Dict
        const [clinicalNoteType] = useDict('ClinicalNoteType');

        // Note dimensions
        const [windowHeight] = useWindowSize();
        const noteTextHeight = windowHeight / 4 > 300 ? 320 : (windowHeight / 4 < 250) ? windowHeight / 5.5 : 260;

        // State
        const [type, setType] = React.useState(disableSave ? 'historical' : 'clinical');
        const [noteBody, setNoteBody] = React.useState('');

        const selectOptions = () => {
            let options = [];
            !_.isEmpty(clinicalNoteType) && clinicalNoteType.forEach(cnote => {
                if ((cnote.edau && isEDAU) || (cnote.ed && !isEDAU))
                    options.push(cnote);
            });
            return options;
        };

        const renderCustomOption = (opt) => {
            return <MenuItem key={opt?.__code} value={opt?.__code}>{t(opt?.__description)}</MenuItem>;
        };

        const noteUpdated = (event) => {
            setNoteBody(event.target.value);
        };

        const renderTextNote = () => {
            return (
                <Grid container spacing={1} justifyContent="space-between">
                    <Grid item xs={12} style={{display: 'flex', flexDirection: 'column', gap: 15}}>
                        <AgyleSelectField
                            onChange={value => setType(value)}
                            label={t('Note Type')}
                            options={selectOptions()}
                            value={type}
                            withBorder
                            withBackground
                            customObject={renderCustomOption}
                            clearButtonVisible
                            defaultClearValue={disableSave ? 'historical' : 'clinical'}
                        />

                        <EditorMarkdownComponent
                            labelClass="txtUp"
                            code={noteBody}
                            onChange={noteUpdated}
                            height={noteTextHeight}
                            ignorePaper={true}
                        />
                    </Grid>
                </Grid>
            );
        };

        const handleClose = () => {
            onClose();
            setNoteBody('');
            setType(disableSave ? 'historical' : 'clinical');
        };

        const addUpdateNewNote = (_event, dataOverwrite) => {
            let newNote = {
                note: dataOverwrite || noteBody,
                recordType: type,
                patientsInWardsRef: [patient.__id],
                episodeId: patient?.admission?.consultantEpisodeId || null,
            };

            if (!isEmpty(fromForm)) {
                newNote['comments'] = [{
                    comment: fromForm,
                    createdBy: userObject?.userName,
                    createdByName: tester,
                }];
            }

            dispatch(saveAgylePreference({defaultNoteType: type}));
            dispatch(addNoteForPatient(patient.patient.__id, newNote, patient.__id, false, forceCurrentAttendance));
            handleClose();
        };

        return (
            <AgyleDialog
                title={t('Add Note')}
                open={open}
                onClose={handleClose}
                fullWidth
                width="md"
                onSave={addUpdateNewNote}
                disableSubmit={!dispatch(hasOneOfPermission([
                        Permissions.AGYLE_CAN_ACCESS_CLINICAL_NOTES,
                        Permissions.AGYLE_CAN_ADD_CLINICAL_NOTES,
                        Permissions.AGYLE_CAN_EDIT_AND_SET_INCORRECT_CLINICAL_NOTES,
                    ],
                ))}
            >
                <>
                    {renderTextNote()}
                </>
            </AgyleDialog>
        );
    }
;

export default NewNoteModal;